import React, { useEffect, useState, useMemo } from "react";
import { api, formatIDR, formatDateTime } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DateRangeFilter, { useDateFilter } from "@/components/DateRangeFilter";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function CashFlow() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  useEffect(() => { api.get("/cashflow").then((r)=>setItems(r.data)); }, []);

  const byDate = useDateFilter(items, range);
  const filtered = useMemo(() => byDate.filter((x) => filter === "all" || x.type === filter), [byDate, filter]);
  const tf = useTableFilter(filtered, {
    searchKeys: ["category", "description", "ref_type"],
    defaultSort: { key: "date", dir: "desc" },
  });
  const stats = useMemo(() => {
    const inn = byDate.filter((x)=>x.type==="in").reduce((s,x)=>s+Number(x.amount),0);
    const out = byDate.filter((x)=>x.type==="out").reduce((s,x)=>s+Number(x.amount),0);
    return { inn, out, bal: inn - out };
  }, [byDate]);

  return (
    <>
      <PageHeader title="Arus Kas" subtitle="Rekap semua kas masuk & keluar dari seluruh transaksi" testId="page-cashflow" />

      <div className="mb-6">
        <DateRangeFilter value={range} onChange={setRange}/>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="stat-card p-5 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-2">Total Kas Masuk</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-success)]">{formatIDR(stats.inn)}</div>
        </Card>
        <Card className="stat-card p-5 border border-[var(--hotto-border)] rounded-md">
          <div className="overline mb-2">Total Kas Keluar</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-destructive)]">{formatIDR(stats.out)}</div>
        </Card>
        <Card className="stat-card p-5 border-2 border-[var(--hotto-primary)] rounded-md">
          <div className="overline mb-2">Saldo</div>
          <div className={`font-mono-num text-2xl font-bold ${stats.bal>=0?"text-[var(--hotto-primary)]":"text-[var(--hotto-destructive)]"}`}>{formatIDR(stats.bal)}</div>
        </Card>
      </div>

      <div className="mb-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
            <TabsTrigger value="in" data-testid="tab-in">Masuk</TabsTrigger>
            <TabsTrigger value="out" data-testid="tab-out">Keluar</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[var(--hotto-border)]">
          <TableToolbar tf={tf} placeholder="Cari kategori / deskripsi..." testId="cashflow-search"/>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="date">Tanggal</SortHeader>
              <SortHeader tf={tf} col="category">Kategori</SortHeader>
              <SortHeader tf={tf} col="description">Deskripsi</SortHeader>
              <SortHeader tf={tf} col="ref_type">Sumber</SortHeader>
              <SortHeader tf={tf} col="amount" className="text-right">Nominal</SortHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada arus kas.</TableCell></TableRow>}
            {tf.rows.map((c, idx) => (
              <TableRow key={c.id} className="table-row-hover">
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono-num text-sm">{formatDateTime(c.date)}</TableCell>
                <TableCell>{c.category}</TableCell>
                <TableCell className="text-slate-600">{c.description}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-xs">{c.ref_type}</Badge></TableCell>
                <TableCell className={`text-right font-mono-num font-semibold ${c.type==="in"?"text-[var(--hotto-success)]":"text-[var(--hotto-destructive)]"}`}>
                  <div className="inline-flex items-center gap-1">
                    {c.type === "in" ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownRight className="w-3 h-3"/>}
                    {c.type==="in"?"+":"-"}{formatIDR(c.amount)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
